/**
 * IUH A/B Test Registry
 *
 * status: 'running' | 'paused' | 'concluded'  — only 'running' tests are bucketed.
 * weights are relative; [50, 50] and [1, 1] behave identically.
 *
 * Concluded tests belong in docs/ai-context/cro-experiments.md, not here.
 *
 * TRAFFIC BUDGET — read before setting anything to 'running'.
 * ~15 orders/month. A winner needs 30+ attributed orders, so each test takes
 * 2-4 months and you get 3-4 per year. Keep EXACTLY ONE test running at a time;
 * two running tests split the same 15 orders and neither ever resolves.
 * Bundle several changes into one bold variant. Button-colour tests are
 * unmeasurable at this volume and will waste a quarter.
 */
window.__IUH_AB_TESTS__ = [
  {
    id: 'pdp_focus',
    name: 'PDP — Remove post-ATC distraction blocks',
    status: 'running',
    started: '2026-07-31',
    pages: ['/products'],
    hypothesis:
      'Organic Search visitors spend 142s on product pages without buying — engaged, ' +
      'not lost. Of the 8 sections that actually render (8 of the 16 in product.json ' +
      'are already disabled), four sit below add-to-cart and pull sideways: a bundle ' +
      'block, two app blocks and recently-viewed. Removing them leaves product, ' +
      'recommendations, reviews and FAQ — halving the page and shortening the decision path.',
    primaryMetric: 'revenue_share_vs_expected',
    variants: [
      { id: 'control', weight: 50 },
      {
        id: 'focused',
        weight: 50,
        // Section ids come from templates/product.json. Shopify wraps each in
        // #shopify-section-<id>, so these selectors are exact, not guesses.
        // Kept: main, product-content, jdgm review grid, faq, ONE rec block.
        // Shopify renders template sections as id="template--<templateId>__<name>",
        // so the bare "#shopify-section-<name>" selector never matched. Ends-with
        // matching is stable across template-id changes.
        css: [
          '[id$="__product-requently"],',
          '[id$="__172519180093bf8f91"],',
          '[id$="__17251918878d49b1fe"],',
          '[id$="__product-recent"]',
          '{display:none!important}'
        ].join(''),
        apply: function (ctx) {
          // If a section is renamed or disabled in the theme editor these stop
          // matching and the test silently becomes a no-op that still consumes
          // the traffic budget. Report the real count so that is visible.
          var suffixes = [
            '__product-requently', '__172519180093bf8f91',
            '__17251918878d49b1fe', '__product-recent'
          ];
          var hit = suffixes.filter(function (sfx) {
            return document.querySelector('[id$="' + sfx + '"]');
          }).length;

          ctx.engine.pushDataLayer('ab_diagnostic', {
            ab_test_id: ctx.test.id,
            sections_hidden: hit,
            sections_expected: suffixes.length
          });

          if (hit < suffixes.length) {
            console.warn('[AB] pdp_focus matched ' + hit + '/' + suffixes.length +
              ' sections — ids may have changed. Test is degraded.');
          }
        }
      }
    ]
  },

  {
    id: 'pdp_certainty',
    name: 'PDP — Certainty block at add-to-cart',
    status: 'paused',
    pages: ['/products'],
    hypothesis:
      'The PDP has no delivery estimate, returns terms or secure-checkout signal ' +
      'anywhere near the buy button. For an unknown brand shipping from China, ' +
      'that uncertainty is what 142-second non-converting sessions look like.',
    primaryMetric: 'revenue_share_vs_expected',
    /**
     * COPY IS LOAD-BEARING AND MUST STAY TRUE.
     * Verified against the live Shopify policies on 2026-07-31:
     *   - Refund policy: "We have a 30-day return policy"  -> claim is accurate
     *   - Actual shipping rates (Admin API, 2026-08-01): only TWO exist —
     *     "Standard Shipping 8-15 days" EUR 6.50 and "Heavy Shipping 20-35 days"
     *     EUR 12.00. Both weight-based. There are ZERO price-based rates and
     *     ZERO free rates in any of the 12 zones.
     *   - Free shipping exists as an automatic DISCOUNT, not a shipping rate:
     *     "Free Shipping Over $100" (DiscountAutomaticFreeShipping), ACTIVE
     *     since 2026-07-06, minimum EUR 100.00.
     * The cart bar advertises EUR 80 (settings.free_shipping_value = 80) and the
     * live cart renders "Free shipping on orders over $80.00 USD". 80 is the
     * intended threshold, so this block quotes 80 to stay consistent with what
     * the customer is already being shown.
     * MISMATCH TO RESOLVE IN SHOPIFY ADMIN: the discount minimum is still 100,
     * so a customer between 80 and 100 is promised free shipping and charged for
     * it at checkout. Lower the discount minimum to 80 to make the advert true.
     * Delivery is stated as a range because the block cannot know the item's
     * weight class: standard is 4-7 processing + 8-15 shipping, heavy is
     * 4-7 + 20-35, so 12-42 days end to end.
     * Do NOT add "we pay return postage" — the policy says returns must be sent
     * back at the customer's cost. Re-verify this block if the policies change.
     */
    variants: [
      { id: 'control', weight: 50 },
      {
        id: 'certainty',
        weight: 50,
        css: [
          '.ab-certainty{border:1px solid rgba(0,0,0,.12);border-radius:6px;',
          'padding:12px 14px;margin:14px 0;background:rgba(0,0,0,.015)}',
          '.ab-certainty__row{display:flex;gap:9px;align-items:flex-start;',
          'padding:4px 0;font-size:13.5px;line-height:1.45;color:#4a4a4a}',
          '.ab-certainty__ic{color:#4a5d4a;font-weight:700;flex:none}',
          '.ab-certainty__row b{color:#1f1f1f;font-weight:600}'
        ].join(''),
        apply: function (ctx) {
          function fmt(d) {
            return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
          }

          function build() {
            if (document.querySelector('.ab-certainty')) return;

            // Anchor on the real markup: sections/product-template-1.liquid
            // wraps quantity + submit in .product-form__quantity-submit.
            var anchor = document.querySelector('.product-form__quantity-submit') ||
                         document.querySelector('.product-form__item--submit');
            if (!anchor) return;

            // Standard: 4-7 processing + 8-15 shipping. Heavy: 4-7 + 20-35.
            // We cannot read the weight class here, so state the standard window
            // and flag that bulky items take longer, rather than promise a date
            // the customer will not get.
            var now = new Date();
            var lo = new Date(now.getTime() + 12 * 86400000);
            var hi = new Date(now.getTime() + 22 * 86400000);

            var box = document.createElement('div');
            box.className = 'ab-certainty';
            box.innerHTML =
              '<div class="ab-certainty__row"><span class="ab-certainty__ic">&rarr;</span>' +
              '<span>Estimated arrival <b>' + fmt(lo) + ' &ndash; ' + fmt(hi) + '</b>' +
              ' &middot; free shipping over &euro;80</span></div>' +
              '<div class="ab-certainty__row"><span class="ab-certainty__ic">&#9202;</span>' +
              '<span>Large or heavy items ship separately and take longer</span></div>' +
              '<div class="ab-certainty__row"><span class="ab-certainty__ic">&#8634;</span>' +
              '<span><b>30-day returns.</b> Contact us within 30 days of delivery.</span></div>' +
              '<div class="ab-certainty__row"><span class="ab-certainty__ic">&#10003;</span>' +
              '<span>Secure checkout &middot; Visa, Mastercard, PayPal</span></div>';

            anchor.parentNode.insertBefore(box, anchor.nextSibling);
            ctx.engine.trackConversion('certainty_block_shown');
          }

          build();
          // PDP re-renders on variant change; re-insert if it gets wiped.
          var form = document.querySelector('.product-form') ||
                     document.querySelector('[data-section-type="product"]');
          if (form) {
            new MutationObserver(function () { build(); })
              .observe(form, { childList: true, subtree: true });
          }
        }
      }
    ]
  },

  {
    id: 'cart_focused_checkout',
    name: 'Cart — Focused Checkout Path',
    // Paused deliberately: /cart sees far less traffic than product pages, so
    // this is the slowest test available. Run it after pdp_focus concludes.
    status: 'paused',
    pages: ['/cart'],
    hypothesis:
      'Collapsing the discount and order-note fields and making checkout the single ' +
      'dominant action reduces cart-stage distraction and lifts cart-to-purchase rate.',
    primaryMetric: 'revenue_share_vs_expected',
    variants: [
      { id: 'control', weight: 50 },
      {
        id: 'focused',
        weight: 50,
        css: [
          '.ab-checkout-cta{font-size:20px!important;padding:22px 50px!important;',
          'width:100%!important;margin-bottom:25px!important;background:#4a5d4a!important;',
          'border-radius:6px!important;font-weight:700!important;text-transform:uppercase!important;',
          'letter-spacing:.5px!important;transition:all .3s ease!important}',
          '.ab-checkout-cta:hover{background:#3a4d3a!important;transform:translateY(-1px)!important;',
          'box-shadow:0 4px 12px rgba(74,93,74,.3)!important}',
          '.ab-reveal{border:none;background:none;text-decoration:underline;cursor:pointer;',
          'color:#666;font-size:14px;margin-bottom:15px;padding:0;display:block}',
          '.ab-reveal:hover{color:#333}'
        ].join(''),
        apply: function (ctx) {
          function collapse(field, label) {
            var el = document.querySelector(field.selector);
            if (!el) return;
            var box = el.closest(field.container);
            if (!box) return;

            box.style.display = 'none';
            var toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'ab-reveal';
            toggle.textContent = label;
            toggle.addEventListener('click', function () {
              box.style.display = 'block';
              toggle.style.display = 'none';
              ctx.engine.trackConversion('cart_field_revealed', { field: field.name });
            });
            box.parentNode.insertBefore(toggle, box);
          }

          function run() {
            var cta = document.querySelector(
              'button[name="checkout"], .cart__checkout-button, [type="submit"][name="checkout"]'
            );
            if (cta) cta.classList.add('ab-checkout-cta');

            collapse(
              {
                name: 'note',
                selector: 'textarea[name="note"]',
                container: '.cart-note, [class*="note"], [class*="instruction"]'
              },
              '+ Add order note'
            );

            collapse(
              {
                name: 'discount',
                selector: 'input[name="discount"], input[id*="coupon"], input[id*="discount"]',
                container: '.cart__discount, [class*="coupon"], [class*="discount"]'
              },
              'Have a discount code?'
            );
          }

          run();
          var target = document.querySelector('.cart, form[action*="/cart"]');
          if (target) {
            new MutationObserver(function () { run(); }).observe(target, {
              childList: true,
              subtree: true
            });
          }
        }
      }
    ]
  }
];
